// Labs — original-chart Phase 3.a (2026-10-04).
//
// Two segments: Queue (open lab requests across the clinic, nurse + doctor
// move them through collect → process → result → release) and Catalogue
// (per-clinic test definitions + pricing; doctor/admin edit, others read).
//
// The 9th top-nav tab. Shares the surface pattern of ProceduresQueueScreen
// (poll 15s, server-sorted) but ships its own screen because labs have a
// catalogue the procedures surface doesn't.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { LabRequestView, LabStatus, LabTest, UpsertLabTestRequest } from '@pamosi/shared';
import { LAB_STATUS_LABELS } from '@pamosi/shared';
import { labsApi, clinicApi } from '../lib/api';

type Segment = 'queue' | 'catalogue';

const STATUS_CLASS: Record<LabStatus, string> = {
    ordered:        'bg-slate-100 text-slate-700',
    collected:      'bg-sky-100 text-sky-800',
    processing:     'bg-indigo-100 text-indigo-800',
    result_entered: 'bg-amber-100 text-amber-800',
    released:       'bg-emerald-100 text-emerald-800',
    cancelled:      'bg-rose-100 text-rose-800',
};

export function LabsScreen() {
    const [segment, setSegment] = useState<Segment>('queue');
    const { data: myClinic } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const role = myClinic?.member?.role ?? null;
    const canEditCatalogue = role === 'doctor' || role === 'admin' || role === 'provider';

    return (
        <div className="space-y-4 max-w-4xl">
            <div>
                <h1 className="text-slate-900 text-2xl font-semibold">{role === 'lab_technician' ? 'Lab workspace' : 'Labs'}</h1>
                <div className="text-slate-500 text-sm mt-1">
                    Open lab orders across the clinic and the catalogue of
                    tests this clinic offers.
                </div>
            </div>

            {role === 'lab_technician' && <div className="pm-card p-4 text-sm text-slate-600">Collect specimens, process requests and enter results for your clinic. Doctors/admins review and release results. Your name and action time are recorded.</div>}
            <div className="pm-card p-1 inline-flex items-center gap-1">
                <SegmentButton active={segment === 'queue'} onClick={() => setSegment('queue')}>
                    Queue
                </SegmentButton>
                <SegmentButton active={segment === 'catalogue'} onClick={() => setSegment('catalogue')}>
                    Catalogue
                </SegmentButton>
            </div>

            {segment === 'queue' && <LabQueueSection />}
            {segment === 'catalogue' && <LabCatalogueSection canEdit={!!canEditCatalogue} />}
        </div>
    );
}

function SegmentButton(
    { active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode },
) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                active
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
        >
            {children}
        </button>
    );
}

// ─── Queue ───────────────────────────────────────────────────────────────

function LabQueueSection() {
    const [showClosed, setShowClosed] = useState(false);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['labs-queue', { showClosed }],
        queryFn: () => labsApi.listQueue({ includeClosed: showClosed }),
        refetchInterval: 15_000,
    });

    return (
        <div className="space-y-3">
            <div className="pm-card p-3 flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={showClosed}
                        onChange={(e) => setShowClosed(e.target.checked)}
                    />
                    Show released / cancelled
                </label>
            </div>

            {isLoading && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    Loading lab queue…
                </div>
            )}
            {isError && (
                <div className="pm-card p-6 text-slate-700 text-sm">
                    Could not load the lab queue.
                </div>
            )}
            {data && data.requests.length === 0 && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    No lab orders match this view.
                </div>
            )}
            {data && data.requests.length > 0 && (
                <ul className="space-y-2">
                    {data.requests.map((r) => (
                        <li key={r.id}>
                            <LabRow request={r} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function LabRow({ request }: { request: LabRequestView }) {
    const qc = useQueryClient();
    const [resultOpen, setResultOpen] = useState(false);
    const [resultDraft, setResultDraft] = useState('');
    const [resultNotice, setResultNotice] = useState('');
    const { data: membership } = useQuery({queryKey:['my-clinic'],queryFn:()=>clinicApi.myClinic()});
    const role = membership?.member?.role;
    const canWork = ['admin','doctor','nurse','provider','lab_technician'].includes(role ?? '');
    const canRelease = ['admin','doctor','provider'].includes(role ?? '');
    const invalidate = () => qc.invalidateQueries({ queryKey: ['labs-queue'] });

    const collect = useMutation({
        mutationFn: () => labsApi.collect(request.id),
        onSuccess: invalidate,
    });
    const process = useMutation({
        mutationFn: () => labsApi.process(request.id),
        onSuccess: invalidate,
    });
    const release = useMutation({
        mutationFn: () => labsApi.release(request.id),
        onSuccess: invalidate,
    });
    const cancel = useMutation({
        mutationFn: (reason: string) => labsApi.cancel(request.id, { reason }),
        onSuccess: invalidate,
    });
    const enterResult = useMutation({
        mutationFn: (summary: string) =>
            labsApi.enterResult(request.id, { result_payload: { summary } }),
        onSuccess: () => { setResultOpen(false); setResultDraft(''); setResultNotice('Result saved. It is ready for doctor/admin review before release.'); void invalidate(); },
    });

    const failure = collect.error || process.error || release.error || cancel.error || enterResult.error;

    function onCancel() {
        const reason = window.prompt('Reason for cancelling this lab order?');
        if (!reason) return;
        cancel.mutate(reason);
    }
    function onEnterResult() {
        enterResult.reset();
        setResultNotice('');
        setResultOpen(true);
    }

    return (
        <div className="pm-card p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-medium text-slate-900">
                        {request.name_at_order}
                        {request.code_at_order && (
                            <span className="ml-2 text-xs font-normal text-slate-500">
                                ({request.code_at_order})
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-slate-600">
                        {request.patient_full_name ?? 'Unknown patient'}
                        {request.hospital_number ? ` · ${request.hospital_number}` : ''}
                    </div>
                    {request.specimen_type && (
                        <div className="text-xs text-slate-500">Specimen: {request.specimen_type}</div>
                    )}
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLASS[request.lab_status]}`}>
                    {LAB_STATUS_LABELS[request.lab_status]}
                </span>
            </div>

            {failure && <p role="alert" className="text-sm text-rose-700">{failure instanceof Error ? failure.message : 'This action failed. Refresh the queue and try again.'}</p>}
            {request.lab_status === 'result_entered' && !canRelease && <p className="text-xs text-amber-800">Result saved · awaiting doctor/admin review and release.</p>}
            {(request.collected_at || request.result_entered_at) && <dl className="text-xs text-slate-500 space-y-1">
                {request.collected_at && <div>Collected by {request.collected_by_full_name || 'Clinic staff'} · {new Date(request.collected_at).toLocaleString()}</div>}
                {request.result_entered_at && <div>Result entered by {request.result_entered_by_full_name || 'Clinic staff'} · {new Date(request.result_entered_at).toLocaleString()}</div>}
            </dl>}
            {resultNotice && <p role="status" className="rounded-lg bg-teal-50 p-3 text-sm text-teal-900">{resultNotice}</p>}
            {resultOpen && canWork && request.lab_status === 'processing' && <form className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 sm:p-5 space-y-4" onSubmit={e => { e.preventDefault(); if (resultDraft.trim()) enterResult.mutate(resultDraft.trim()); }}>
                <header><h3 className="text-lg font-semibold text-slate-900">Enter laboratory result</h3><p className="text-sm text-slate-600 mt-1">{request.patient_full_name ?? 'Unknown patient'}{request.hospital_number ? ` · ${request.hospital_number}` : ''}</p><p className="text-sm font-medium text-teal-800">{request.name_at_order}{request.specimen_type ? ` · ${request.specimen_type}` : ''}</p></header>
                <label className="block text-sm font-medium text-slate-800" htmlFor={`lab-result-${request.id}`}>Result / findings <span className="text-rose-600">*</span></label>
                <textarea id={`lab-result-${request.id}`} className="pm-input w-full min-h-[240px] resize-y leading-relaxed bg-white" rows={10} autoFocus required value={resultDraft} onChange={e => setResultDraft(e.target.value)} disabled={enterResult.isPending} placeholder="Enter or paste the laboratory findings. Use a new line for each measurement, including units and reference ranges where applicable." aria-describedby={`lab-result-help-${request.id}`} />
                <p id={`lab-result-help-${request.id}`} className="text-xs text-slate-600">Your name and save time are recorded. Saving sends the result for review; it does not release it to the patient.</p>
                <div className="flex flex-wrap gap-3"><button type="submit" className="pm-btn-primary" disabled={!resultDraft.trim() || enterResult.isPending}>{enterResult.isPending ? 'Saving result…' : 'Save result for review'}</button><button type="button" className="pm-btn-outline" disabled={enterResult.isPending} onClick={() => { if (!resultDraft.trim() || window.confirm('Discard this unsaved result?')) { setResultOpen(false); setResultDraft(''); enterResult.reset(); } }}>Cancel</button></div>
                {enterResult.error && <p role="alert" className="text-sm text-rose-700">Could not save the result. Your entry is still here; try again or contact your clinic admin.</p>}
            </form>}
            <div className="flex items-center gap-2 flex-wrap">
                {canWork && request.lab_status === 'ordered' && (
                    <button
                        className="pm-btn-primary text-sm"
                        onClick={() => collect.mutate()}
                        disabled={collect.isPending}
                    >
                        Mark collected
                    </button>
                )}
                {canWork && request.lab_status === 'collected' && (
                    <button
                        className="pm-btn-primary text-sm"
                        onClick={() => process.mutate()}
                        disabled={process.isPending}
                    >
                        Move to processing
                    </button>
                )}
                {canWork && request.lab_status === 'processing' && !resultOpen && (
                    <button
                        className="pm-btn-primary text-sm"
                        onClick={onEnterResult}
                        disabled={enterResult.isPending}
                    >
                        Enter result
                    </button>
                )}
                {canRelease && request.lab_status === 'result_entered' && (
                    <button
                        className="pm-btn-primary text-sm"
                        onClick={() => release.mutate()}
                        disabled={release.isPending}
                    >
                        Release to patient
                    </button>
                )}
                {canRelease && ['ordered', 'collected', 'processing', 'result_entered'].includes(request.lab_status) && (
                    <button
                        className="pm-btn-ghost text-sm text-rose-600"
                        onClick={onCancel}
                        disabled={cancel.isPending}
                    >
                        Cancel
                    </button>
                )}
                {request.result_payload?.summary && (
                    <div className="w-full whitespace-pre-wrap break-words text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 mt-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1">Result:</span>
                        {request.result_payload.summary}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Catalogue ───────────────────────────────────────────────────────────

function LabCatalogueSection({ canEdit }: { canEdit: boolean }) {
    const [editing, setEditing] = useState<LabTest | 'new' | null>(null);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['lab-catalogue'],
        queryFn: () => labsApi.listCatalogue({ includeInactive: true }),
    });

    return (
        <div className="space-y-3">
            {canEdit && (
                <div>
                    <button className="pm-btn-primary text-sm" onClick={() => setEditing('new')}>
                        + Add test
                    </button>
                </div>
            )}
            {isLoading && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    Loading catalogue…
                </div>
            )}
            {isError && (
                <div className="pm-card p-6 text-slate-700 text-sm">
                    Could not load the catalogue.
                </div>
            )}
            {data && data.tests.length === 0 && (
                <div className="pm-card p-6 text-slate-500 text-sm text-center">
                    No tests in this catalogue yet.
                </div>
            )}
            {data && data.tests.length > 0 && (
                <ul className="space-y-2">
                    {data.tests.map((t) => (
                        <li key={t.id}>
                            <CatalogueRow test={t} canEdit={canEdit} onEdit={() => setEditing(t)} />
                        </li>
                    ))}
                </ul>
            )}
            {editing && (
                <CatalogueEditor
                    initial={editing === 'new' ? null : editing}
                    onDone={() => setEditing(null)}
                />
            )}
        </div>
    );
}

function CatalogueRow(
    { test, canEdit, onEdit }: { test: LabTest; canEdit: boolean; onEdit: () => void },
) {
    const qc = useQueryClient();
    const del = useMutation({
        mutationFn: () => labsApi.deleteCatalogue(test.id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['lab-catalogue'] }),
    });

    const price =
        test.price_kobo == null
            ? '—'
            : `₦${(test.price_kobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
    const priceLabel = test.is_placeholder_price ? `${price} (placeholder)` : price;

    return (
        <div className="pm-card p-4 flex items-start justify-between gap-3">
            <div>
                <div className="font-medium text-slate-900">
                    {test.name}
                    <span className="ml-2 text-xs font-normal text-slate-500">({test.code})</span>
                    {!test.active && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-500">
                            Inactive
                        </span>
                    )}
                </div>
                <div className="text-sm text-slate-600 flex items-center gap-3 flex-wrap">
                    <span>{priceLabel}</span>
                    {test.specimen_type && <span>· {test.specimen_type}</span>}
                    {test.turnaround_hours != null && (
                        <span>· TAT {test.turnaround_hours}h</span>
                    )}
                </div>
            </div>
            {canEdit && (
                <div className="flex items-center gap-2">
                    <button className="pm-btn-ghost text-sm" onClick={onEdit}>
                        Edit
                    </button>
                    <button
                        className="pm-btn-ghost text-sm text-rose-600"
                        onClick={() => {
                            if (window.confirm(`Delete "${test.name}"?`)) del.mutate();
                        }}
                        disabled={del.isPending}
                    >
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
}

function CatalogueEditor({ initial, onDone }: { initial: LabTest | null; onDone: () => void }) {
    const [code, setCode] = useState(initial?.code ?? '');
    const [name, setName] = useState(initial?.name ?? '');
    const [specimen, setSpecimen] = useState(initial?.specimen_type ?? '');
    const [turnaround, setTurnaround] = useState(initial?.turnaround_hours?.toString() ?? '');
    const [priceNaira, setPriceNaira] = useState(
        initial?.price_kobo != null ? (initial.price_kobo / 100).toString() : '',
    );
    const [active, setActive] = useState(initial?.active ?? true);
    const [error, setError] = useState<string | null>(null);

    const qc = useQueryClient();
    const save = useMutation({
        mutationFn: (body: UpsertLabTestRequest) => labsApi.upsertCatalogue(body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['lab-catalogue'] });
            onDone();
        },
        onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Save failed'),
    });

    function submit() {
        setError(null);
        if (!code.trim() || !name.trim()) {
            setError('Code and name are required');
            return;
        }
        const priceKobo = priceNaira.trim() === '' ? null : Math.round(Number(priceNaira) * 100);
        if (priceKobo != null && (!Number.isFinite(priceKobo) || priceKobo < 0)) {
            setError('Invalid price');
            return;
        }
        const turnaroundHours = turnaround.trim() === '' ? null : Number(turnaround);
        if (turnaroundHours != null && (!Number.isInteger(turnaroundHours) || turnaroundHours < 0)) {
            setError('Invalid turnaround hours');
            return;
        }
        save.mutate({
            ...(initial ? { id: initial.id } : {}),
            code: code.trim(),
            name: name.trim(),
            specimen_type: specimen.trim() || null,
            turnaround_hours: turnaroundHours,
            price_kobo: priceKobo,
            is_placeholder_price: priceKobo == null,
            active,
        });
    }

    return (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50">
            <div className="pm-card p-6 w-full max-w-md space-y-3">
                <h2 className="text-lg font-semibold text-slate-900">
                    {initial ? 'Edit test' : 'Add test'}
                </h2>
                <Field label="Code">
                    <input
                        className="pm-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="fbc"
                    />
                </Field>
                <Field label="Name">
                    <input
                        className="pm-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Full Blood Count"
                    />
                </Field>
                <Field label="Specimen type">
                    <input
                        className="pm-input"
                        value={specimen}
                        onChange={(e) => setSpecimen(e.target.value)}
                        placeholder="Whole blood (EDTA)"
                    />
                </Field>
                <Field label="Turnaround (hours)">
                    <input
                        className="pm-input"
                        value={turnaround}
                        onChange={(e) => setTurnaround(e.target.value)}
                        placeholder="2"
                        inputMode="numeric"
                    />
                </Field>
                <Field label="Price (₦)">
                    <input
                        className="pm-input"
                        value={priceNaira}
                        onChange={(e) => setPriceNaira(e.target.value)}
                        placeholder="Leave blank to flag as unpriced"
                        inputMode="decimal"
                    />
                </Field>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                    />
                    Active (orderable)
                </label>
                {error && <div className="text-sm text-rose-600">{error}</div>}
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button className="pm-btn-ghost" onClick={onDone} disabled={save.isPending}>
                        Cancel
                    </button>
                    <button className="pm-btn-primary" onClick={submit} disabled={save.isPending}>
                        {save.isPending ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label className="block space-y-1">
            <span className="text-xs font-semibold text-slate-600">{label}</span>
            {children}
        </label>
    );
}
